import type { SellerForm as SellerFormValues } from '@gozoki/api'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import z from 'zod/v3'
import { SellerForm } from '~/components/routes/sellers/SellerForm'
import { Spinner } from '~/components/ui/spinner'
import { api } from '~/lib/tuyau'
import type { PermissionTuple } from '~/utils/ability'
import { usePermissionGuard, withRoutePermissions } from '~/utils/rbac'
import { getApiErrorMessage } from '~/lib/utils'

const EDIT_SELLER: PermissionTuple = ['edit', 'sellers.sellers.seller']
const READ_SELLER: PermissionTuple = ['read', 'sellers.sellers.seller']

// L'écran sert aussi de fiche en lecture seule (« Voir ») : l'un OU l'autre droit l'ouvre.
export const Route = createFileRoute('/_authenticated/sellers/$sellerId/edit')({
  staticData: {
    permission: { any: [EDIT_SELLER, READ_SELLER] },
  },
  params: {
    parse: z.object({ sellerId: z.coerce.number() }).parse,
  },
  component: EditSellerPage,
})
withRoutePermissions(Route)

/** Seules les colonnes lues par le formulaire — évite de dépendre du type complet du modèle. */
interface EditableSeller {
  reference: string | null
  label: string
  address?: {
    address: string | null
    addressAdditionnal: string | null
    postalCode: string | null
    city: string | null
  } | null
}

const toFormValues = (seller: EditableSeller): SellerFormValues => ({
  reference: seller.reference ?? '',
  label: seller.label,
  address: seller.address?.address ?? '',
  addressAdditionnal: seller.address?.addressAdditionnal ?? '',
  postalCode: seller.address?.postalCode ?? '',
  city: seller.address?.city ?? '',
})

function EditSellerPage() {
  const { sellerId } = Route.useParams()
  const navigate = useNavigate()
  const canEdit = usePermissionGuard(EDIT_SELLER).authorized

  const { data: seller, isLoading } = useQuery(
    api.sellers.show.queryOptions({ params: { id: sellerId } }),
  )
  const { mutateAsync: update } = useMutation(api.sellers.update.mutationOptions({}))

  const navigateBack = () => navigate({ to: '/sellers/sellers' })

  if (isLoading) {
    return (
      <div className="flex justify-center p-16">
        <Spinner />
      </div>
    )
  }

  if (!seller) {
    return <div className="text-muted-foreground p-16 text-center">Vendeur introuvable.</div>
  }

  return (
    <SellerForm
      key={seller.id}
      title={canEdit ? 'Modifier un vendeur' : 'Voir un vendeur'}
      submitLabel="Modifier"
      canEdit={canEdit}
      defaultValues={toFormValues(seller)}
      onCancel={navigateBack}
      onSubmit={async (values) => {
        try {
          await update({ params: { id: sellerId }, body: values })
          navigateBack()
        } catch (error) {
          toast.error(getApiErrorMessage(error))
        }
      }}
    />
  )
}
